import { z } from 'zod';
import { candidateSchema } from './candidates.js';
import { idSchema, instantSchema } from './common.js';
import { stageRefSchema } from './projects.js';
/**
 * Shortlists (RELAY-951): the candidates put forward for a job, in batches. A job has a DEFAULT
 * shortlist from the moment it exists, so a single-batch job needs no extra step; further
 * batches are more shortlists. A candidate sits on a shortlist in a stage of the Shortlist Flow
 * (the `shortlist` pipeline type since TS-1888, by stage id), may sit on several shortlists of
 * the same job, and once per shortlist.
 *
 * Every stage change writes a `shortlist_candidate_stage_history` row — Relay's
 * `recruiting.candidate_stage_history`, with stage ids for the text stages — carrying its
 * source and actor, in the same transaction as the change, its audit row and its activity row.
 * The Candidate → Jobs tab and the Job → Shortlists tab both read `shortlist_candidates`, so
 * they cannot disagree.
 *
 * A hire fills an opening (TS-925): a candidate placed in a won stage (Hired) adds one to the
 * job's `filled`, and is refused (409) when every opening is filled. A hire marked as a
 * REPLACEMENT refills a position someone already filled and takes none; the choice is made when
 * the candidate is moved into the won stage (`replacement`). Leaving the won stage, or the
 * shortlist, gives a counted hire's opening back.
 *
 * Taking a candidate off a shortlist ARCHIVES the place (`:archive`): it keeps its stage and
 * history but leaves the shortlist's lists and board (and a counted hire's opening is given back)
 * until it is restored (`:restore`, or adding the candidate again). An archived place cannot be
 * moved or edited.
 */
/**
 * The website marketplace's candidate code (`RL-8K9TX`): Relay's `resumes.public_code`, which
 * Relay writes to RecruitCRM's candidate field 46 "candidate_public_id" and the CRM imports as the
 * candidate custom field of that key (TS-1753). A shortlist opens on the marketplace as
 * `/marketplace/shortlist/?candidates=RL-A,RL-B` (the website's `MarketplaceLink::shortlist`).
 */
export const MARKETPLACE_CODE_FIELD_KEY = 'candidate_public_id';
/** What the website accepts as a code (`InterviewLink`); anything else is left out of a link. */
export const MARKETPLACE_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
/** The most codes one marketplace shortlist link takes (`TalentClient::SHORTLIST_LIMIT`). */
export const MARKETPLACE_SHORTLIST_MAX = 50;
export const SHORTLIST_STATUSES = ['draft', 'presented', 'closed'];
export const shortlistStatusSchema = z.enum(SHORTLIST_STATUSES);
/**
 * Where a stage change came from. `ui` and `import` are declared by the caller with the
 * `X-Change-Source` header (the web app always sends `ui`); without it a request is `api`.
 * `rule` is written only by the platform itself (roll-up rules and automations), never accepted
 * from a request. Relay's nine sources map onto these four — see the data dictionary.
 */
export const STAGE_CHANGE_SOURCES = ['ui', 'api', 'import', 'rule'];
export const stageChangeSourceSchema = z.enum(STAGE_CHANGE_SOURCES);
export const REQUEST_CHANGE_SOURCES = ['ui', 'api', 'import'];
export const CHANGE_SOURCE_HEADER = 'x-change-source';
export const shortlistInputSchema = z.object({ name: z.string().trim().min(1).max(120) });
export const shortlistPatchSchema = z
    .object({ name: z.string().trim().min(1).max(120), status: shortlistStatusSchema })
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const shortlistCandidateSchema = z.object({
    shortlistId: idSchema,
    jobId: idSchema,
    candidateId: idSchema,
    candidate: z.object({
        id: idSchema,
        name: z.string(),
        email: z.string().nullable(),
        /** The resume, so a shortlist row can open it without reading the candidate (the viewer's
         * rule: the CRM document first, else the RecruitCRM / Relay link). */
        resumeDocument: candidateSchema.shape.resumeDocument,
        resumeFileLink: z.string().nullable(),
        resumeFilename: z.string().nullable(),
        /** The marketplace code (`MARKETPLACE_CODE_FIELD_KEY`), null when unset, invalid or hidden. */
        marketplaceCode: z.string().nullable(),
    }),
    stageId: idSchema,
    stage: stageRefSchema,
    /** When the candidate entered the stage they are in now. */
    stageEnteredAt: instantSchema,
    visibleToClient: z.boolean(),
    rejectedReason: z.string().nullable(),
    /** A hire that replaces someone, taking no opening; only ever true in a won stage. */
    isReplacement: z.boolean(),
    /**
     * When the candidate was taken off the shortlist (archived), null while on it. Archived places
     * are only served when asked for (`includeArchived`); restoring one puts it back as it was.
     */
    archivedAt: instantSchema.nullable(),
    addedAt: instantSchema,
});
export const shortlistSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    name: z.string(),
    status: shortlistStatusSchema,
    /** The job's first shortlist, created with it; it cannot be deleted. */
    isDefault: z.boolean(),
    presentedAt: instantSchema.nullable(),
    createdBy: idSchema.nullable(),
    createdAt: instantSchema,
    candidates: z.array(shortlistCandidateSchema),
});
/** `?includeArchived=true` on a shortlist read also lists the archived places. */
export const includeArchivedQuerySchema = z.object({
    includeArchived: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true')
        .meta({ description: 'Also list the candidates taken off (archived); false when absent' }),
});
export const shortlistCandidateInputSchema = z.object({
    candidateId: idSchema,
    /** A Shortlist Flow stage; the default Shortlist Flow's first open stage when omitted. */
    stageId: idSchema.optional(),
    visibleToClient: z.boolean().default(false),
    /** With a won stage only: the hire replaces someone and takes no opening. */
    replacement: z.boolean().default(false),
});
/** No defaults: a PATCH changes only what it names. */
export const shortlistCandidatePatchSchema = z
    .object({
    stageId: idSchema,
    visibleToClient: z.boolean(),
    /** Kept only while the candidate is in a lost stage; moving out of one clears it. */
    rejectedReason: z.string().trim().max(500).nullable(),
    /**
     * In (or into) a won stage only: the hire replaces someone and takes no opening. Omitted, a
     * move into a won stage is a hire that fills one, and a place already there keeps its kind.
     */
    replacement: z.boolean(),
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const candidateStageHistoryEntrySchema = z.object({
    id: idSchema,
    oldStageId: idSchema.nullable(),
    newStageId: idSchema,
    source: stageChangeSourceSchema,
    changedBy: idSchema.nullable(),
    /** The job's recruiter when the change happened (Relay's `t1_recruiter_snapshot`). */
    recruiterSnapshotId: idSchema.nullable(),
    createdAt: instantSchema,
});
/** One line of a candidate's Jobs tab: a job they are shortlisted on, per shortlist. */
export const candidateJobSchema = z.object({
    job: z.object({ id: idSchema, title: z.string(), projectId: idSchema }),
    shortlist: z.object({ id: idSchema, name: z.string(), status: shortlistStatusSchema }),
    stageId: idSchema,
    stage: stageRefSchema,
    stageEnteredAt: instantSchema,
    visibleToClient: z.boolean(),
    rejectedReason: z.string().nullable(),
    addedAt: instantSchema,
});
//# sourceMappingURL=shortlists.js.map